import { Link } from "@/lib/nav";
import type { ReactNode } from "react";

import CustomerScreenShell from "@/components/customer-screen-shell";
import SectionCard, { sectionCardClass } from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { formatNumber } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import { intakeFromPublicPath, withVia } from "@buildpy/core/modules/customer/intake-page";
import { publicPortfolioDetailPath, publicPortfolioPath } from "@buildpy/core/modules/portfolio/public-view";
import { publicCaseTraits } from "@/modules/portfolio/ui/public-case-grid";
import PublicPhoto from "@/modules/portfolio/ui/public-photo";
import type { PublicPageStoryView } from "@buildpy/core/modules/public-page/link-service";
import {
  HeroPhotos,
  PublicPageCta,
  PublicPageDesigners,
  PublicPageEmpty,
  PublicPageFooter,
  PublicPageHeader,
  PublicPageReviews,
  PublicPageTrustCard,
} from "@/modules/public-page/ui/public-page-sections";
import type { PublicPageStory as Story, PublicPageTrust } from "@buildpy/core/modules/public-page/view";

const stepsOf = (trust: PublicPageTrust): { title: string; note: string }[] => [
  { title: "상담", note: "현장 방문 · 실측" },
  { title: "견적", note: "공정별 상세견적 · 고칠 때마다 판 번호" },
  { title: "표준계약", note: "표준계약서 · 링크로 확인" },
  { title: "시공", note: "매일 사진 · 현장일지를 고객 링크로" },
  {
    title: "준공 · 하자보증",
    note: trust.warranty_months !== null ? `준공 후 ${formatNumber(trust.warranty_months)}개월` : "준공 후 하자보증",
  },
];

const factsOf = (traits: string, story: Story): string =>
  [traits || null, story.chapter_count !== null ? `공정 ${formatNumber(story.chapter_count)}개` : null, story.period]
    .filter((v): v is string => Boolean(v))
    .join(" · ");

// 공개 링크 B(이야기) — 대표 현장 사진 → 일하는 방식 → 대표 현장(전/후·일지 한 줄) → 다른 현장 → 신뢰 지표 → 후기 → 디자이너 → 상담 문의.
const PublicPageStory = ({ view, footer }: { view: PublicPageStoryView; footer?: ReactNode }) => {
  const { org, code, hero, trust, reviews, designers, story, show_inquiry } = view;
  const inquiry = show_inquiry ? intakeFromPublicPath(org.slug, code, null) : null;
  const detailOf = (id: string) => withVia(publicPortfolioDetailPath(org.slug, id), code);
  const shown = (hero ? 1 : 0) + story.others.length;
  const facts = hero ? factsOf(publicCaseTraits(hero), story) : "";

  return (
    <CustomerScreenShell className="p-0 pb-10">
      <PublicPageHeader inquiry={inquiry} org={org} />

      <div className="mx-auto flex w-full max-w-5xl flex-col gap-y-8 px-4 pt-4">
        {hero ? (
          <SectionCard as="div" className="overflow-hidden" data-testid="public-page-story-intro" padding="none">
            <PublicPhoto alt={hero.title} className="aspect-[16/9] w-full" src={hero.after_src ?? hero.cover_src} />
            <div className="flex flex-col items-start gap-y-3 p-4 sm:p-6">
              <h2 className="text-xl font-semibold">문의부터 준공까지, 기록으로 보여 드립니다</h2>
              {inquiry && (
                <Button asChild>
                  <Link href={inquiry}>우리 집도 상담받기</Link>
                </Button>
              )}
            </div>
          </SectionCard>
        ) : (
          <PublicPageEmpty />
        )}

        <SectionCard data-testid="public-page-steps" description="다섯 단계, 매 단계를 남깁니다" title="일하는 방식">
          <ol className="grid grid-cols-1 gap-3 sm:grid-cols-5">
            {stepsOf(trust).map((step, i) => (
              <li key={step.title} className="flex flex-col gap-y-0.5 rounded-md bg-muted p-3">
                <span className="text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm font-semibold">{step.title}</span>
                <span className="text-xs text-muted-foreground">{step.note}</span>
              </li>
            ))}
          </ol>
        </SectionCard>

        {hero && (
          <SectionCard data-testid="public-page-hero" title="대표 현장">
            <div className="flex flex-col gap-y-3">
              <div className="overflow-hidden rounded-md">
                <HeroPhotos afterLabel="준공" hero={hero} />
              </div>
              <h3 className="text-lg font-semibold">{hero.title}</h3>
              {facts && <p className="text-sm text-muted-foreground">{facts}</p>}
              {hero.summary && <p className="text-sm text-muted-foreground">{hero.summary}</p>}
              {story.excerpt && (
                <figure className="flex flex-col gap-y-1 rounded-md bg-muted p-3" data-testid="public-page-story-excerpt">
                  <blockquote className="whitespace-pre-line text-sm">{story.excerpt.text}</blockquote>
                  <figcaption className="text-xs text-muted-foreground">{story.excerpt.caption}</figcaption>
                </figure>
              )}
              <Link className="text-sm font-medium underline-offset-2 hover:underline" href={detailOf(hero.id)}>
                이 현장 전체 보기
              </Link>
            </div>
          </SectionCard>
        )}

        {story.others.length > 0 && (
          <section className="flex flex-col gap-y-3" data-testid="public-page-story-others">
            <h2 className="text-base font-semibold">다른 현장</h2>
            <ul className="flex flex-col gap-y-4">
              {story.others.map((card, i) => {
                const traits = publicCaseTraits(card);
                return (
                  <li key={card.id}>
                    <Link
                      className={cn(
                        sectionCardClass({ padding: "none" }),
                        "group flex flex-col overflow-hidden sm:flex-row",
                        i % 2 === 1 && "sm:flex-row-reverse"
                      )}
                      href={detailOf(card.id)}>
                      <PublicPhoto alt={card.title} className="aspect-[4/3] w-full sm:w-1/2" src={card.cover_src} />
                      <div className="flex flex-1 flex-col justify-center gap-y-1.5 p-4">
                        <h3 className="text-base font-semibold group-hover:underline">{card.title}</h3>
                        {traits && <p className="text-sm text-muted-foreground">{traits}</p>}
                        {card.summary && <p className="line-clamp-3 text-sm text-muted-foreground">{card.summary}</p>}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {story.total > shown && (
          <Link
            className="self-center text-sm font-medium underline-offset-2 hover:underline"
            data-testid="public-page-story-all"
            href={withVia(publicPortfolioPath(org.slug), code)}>
            시공사례 {formatNumber(story.total)}건 모두 보기
          </Link>
        )}

        <PublicPageTrustCard trust={trust} />
        <PublicPageReviews reviews={reviews} />
        <PublicPageDesigners designers={designers} title="함께할 디자이너" />
        {inquiry && (
          <PublicPageCta inquiry={inquiry} note="이름과 연락처만 남기면 됩니다. 1분이면 끝납니다." title="우리 집도 상담받기" />
        )}
        <PublicPageFooter org={org} />
      </div>
      {footer}
    </CustomerScreenShell>
  );
};

export default PublicPageStory;
